/**
 * ChatPanel Component
 *
 * Main chat interface for the full-screen AI Assistant.
 * Uses shared chat components and session management hook.
 * Configurable to work with different API clients (Reports, Subject Management, etc.)
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Button, Dropdown } from 'antd';
import ChatMessageList from '../reports-ai-core/ChatMessageList';
import ChatInputArea from '../reports-ai-core/ChatInputArea';
import { useChatSession } from '../reports-ai-core/useChatSession';
import ApiReportsChatbot from '../../../../api/reports-chatbot';
import ApiSubjectManagementAi from '../../../../api/subjectManagementAi';
import ApiSubjectsManagement from '../../../../api/subjects-manager';
import { useReportsProgressTracking } from '../reports-ai-core/useReportsProgressTracking';
import exportPdfIcon from '../../../../assets/export-pdf.svg';
import chatEmptyIcon from '../../../../assets/chat-empty.svg';
import dotsHorizontalIcon from '../../../../assets/dots-horizontal.svg';
import '../reports-chatbot/reports-chatbot.css';

const ChatPanel = ({
  sessionId,
  userId,
  context,
  onTitleUpdated,
  apiClient = ApiReportsChatbot,
  progressTrackingHook = useReportsProgressTracking,
  title = 'Reports AI Assistant',
  sessionSource = null
}) => {
  // For backwards compatibility
  const reportContext = context;
  const [inputMessage, setInputMessage] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [recentSubjects, setRecentSubjects] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);

  // Determine initial context based on sessionSource
  const initialContext = useMemo(() => {
    if (sessionSource === 'subject-management') {
      return { type: 'subject' };
    }
    return { type: 'reports' };
  }, [sessionSource]);

  const {
    messages,
    loading,
    error,
    stageName,
    elapsedSeconds,
    loadSession,
    sendMessage,
    cancelRequest
  } = useChatSession(
    userId,
    reportContext,
    onTitleUpdated,
    apiClient, // Default API for session management
    progressTrackingHook,
    null, // welcomeMessage
    sessionSource,
    ApiReportsChatbot, // reportsApiClient for dynamic routing
    ApiSubjectManagementAi // subjectApiClient for dynamic routing
  );

  // Load session when sessionId changes
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    } else {
      loadSession(null);
    }
  }, [sessionId, loadSession]);

  // Fetch recent subjects for autocomplete
  useEffect(() => {
    const fetchRecentSubjects = async () => {
      try {
        const response = await ApiSubjectsManagement.getSubjects({
          limit: 20,
          sortBy: 'updatedAt',
          sortOrder: 'desc'
        });
        // Handle both response.data.subjects and response.data.data.subjects
        const subjects = response.data?.data?.subjects || response.data?.subjects || [];
        setRecentSubjects(subjects);
      } catch (err) {
        console.error('[ChatPanel] Failed to fetch recent subjects:', err);
        // Don't break the UI if subjects fetch fails
        setRecentSubjects([]);
      }
    };

    fetchRecentSubjects();
  }, []);

  const handleSendMessage = (messageOverride, files = []) => {
    const messageToSend = messageOverride !== undefined ? messageOverride : inputMessage;
    if (!messageToSend.trim() && files.length === 0) return;
    if (loading) return;
    console.log('[ChatPanel] Sending message:', {
      message: messageToSend.substring(0, 100),
      filesCount: files.length,
      files: files.map(f => ({ name: f.name, type: f.type, size: f.size }))
    });
    sendMessage(messageToSend, files);
    setInputMessage('');
    setAttachedFiles([]);
  };

  // Export session as PDF (matches modal implementation)
  const handleExportSession = async () => {
    if (!sessionId || !userId) return;

    setIsExporting(true);
    setExportError(null);
    try {
      const pdfBlob = await apiClient.exportSession(sessionId, userId);

      // Create download link
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
      const sanitizedTitle = title.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
      const filename = `${sanitizedTitle}_${timestamp}.pdf`;
      link.download = filename;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export session:', err);
      setExportError('Failed to export session. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Menu items for dropdown
  const menuItems = [
    {
      key: 'export',
      label: 'Export as PDF',
      icon: <img src={exportPdfIcon} alt="Export" width="16" height="16" />,
      disabled: isExporting || messages.length === 0,
      onClick: ({ domEvent }) => {
        domEvent?.stopPropagation();
        handleExportSession();
      }
    }
  ];

  // No session selected
  if (!sessionId) {
    return (
      <div className="chat-panel">
        <div className="chat-panel__empty">
          <img src={chatEmptyIcon} alt="No session" width="64" height="64" style={{ opacity: 0.3 }} />
          <h3>Select a session to start</h3>
          <p>Choose a session from the sidebar or create a new one</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-panel" data-testid="ai-chat">
      {/* Header */}
      <div className="chat-panel__header">
        {title && (
          <div className="chat-panel__header-title">
            <h2>{title}</h2>
          </div>
        )}
        <div className="chat-panel__header-actions">
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<img src={dotsHorizontalIcon} alt="More actions" width="20" height="20" />}
              className="chatbot-action-menu"
            />
          </Dropdown>
        </div>
      </div>

      {/* Messages */}
      <div className="chat-panel__messages">
        <ChatMessageList
          messages={messages}
          loading={loading}
          stageName={stageName}
          elapsedSeconds={elapsedSeconds}
          onCancelRequest={cancelRequest}
          sessionSource={sessionSource}
        />
      </div>

      {/* Error banners */}
      {error && (
        <div className="chat-panel__error" data-testid="ai-chat-error">
          {error}
        </div>
      )}
      {exportError && (
        <div className="chat-panel__error">
          {exportError}
        </div>
      )}

      {/* Input area - reuse same structure as modal chatbot */}
      <ChatInputArea
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onSend={handleSendMessage}
        disabled={loading}
        placeholder="Ask about your data... (try @reports: or @subject:email:)"
        recentSubjects={recentSubjects}
        initialContext={initialContext}
        attachedFiles={attachedFiles}
        onFilesChange={setAttachedFiles}
      />
    </div>
  );
};

export default ChatPanel;
